import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Receipt,
  User,
  Target,
  FileText,
  DollarSign,
} from 'lucide-react';
import { useToast } from '../common/Toast';
import API_CONFIG from '../../config/api';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const ViewInvoice = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const { error: showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);

  useEffect(() => {
    const loadInvoice = async () => {
      setLoading(true);
      // If the invoice was passed via navigation state, normalize it and skip fetching
      if (location?.state?.invoice) {
        const src = location.state.invoice || null;
        if (src) {
          const normalized = {
            // keep original fields
            ...src,
            // mapping to the view's expected fields
            subtotal:
              typeof src.subtotal === 'number'
                ? src.subtotal
                : typeof src.amount === 'number'
                  ? src.amount
                  : src.subtotal,
            total:
              typeof src.total === 'number'
                ? src.total
                : typeof src.amount === 'number'
                  ? src.amount
                  : src.total,
            items: Array.isArray(src.items) ? src.items : [],
            clientName: src.counterparty || src.clientName || src.company || '',
            clientEmail: src.clientEmail || src.email || '',
            clientPhone: src.clientPhone || src.phone || '',
            clientAddress: src.clientAddress || src.address || '',
            projectName: src.contractTitle || src.projectName || '',
            date: src.date || src.createdAt || src.invoiceDate || null,
            dueDate: src.dueDate || src.dueAt || null,
            notes: src.notes || src.memo || '',
            description: src.description || src.projectDescription || '',
          };
          setInvoice(normalized);
        } else {
          setInvoice(null);
        }
        setLoading(false);
        return;
      }
      try {
        const response = await fetch(
          `${API_CONFIG.ENDPOINTS.PROJECT_INVOICES}/${id}`,
          {
            headers: API_CONFIG.HEADERS,
          },
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Failed to load invoice');
        }

        const data = await response.json();
        setInvoice(data.data || null);
      } catch (err) {
        showError(err.message || 'Failed to load invoice');
      } finally {
        setLoading(false);
      }
    };

    loadInvoice();
  }, [id]);

  if (loading) {
    return (
      <>
        <PageBreadcrumb />
        <PageHeader
          title="Invoice Details"
          description="Loading invoice details..."
        />
        <div className="page-container">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: '400px' }}
          >
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!invoice) {
    return (
      <>
        <PageBreadcrumb />
        <PageHeader title="Invoice Details" description="Invoice not found" />
        <div className="page-container">
          <button
            type="button"
            className="btn btn-secondary d-flex align-items-center"
            onClick={() => navigate('/projects/time-tracking/billing')}
          >
            <ArrowLeft size={16} className="me-2" />
            Back to Billing
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Invoice Details"
        description="Review invoice information and line items"
      />

      {/* Tab Styles */}
      <style>
        {`
          .invoice-tabs-container {
            border-bottom: 1px solid #e5e7eb;
            margin-bottom: 16px;
          }

          .invoice-tabs {
            display: flex;
            gap: 6px;
            padding: 0 8px;
          }

          .invoice-tab {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 8px 12px;
            font-size: 13px;
            font-weight: 500;
            color: #6b7280;
            background: transparent;
            border: none;
            border-bottom: 2px solid transparent;
            border-radius: 0;
            cursor: pointer;
            transition: all 0.15s ease;
          }

          .invoice-tab.active {
            color: #1e40af;
            border-bottom-color: #3b82f6;
          }

          .invoice-tab:hover:not(.active) {
            color: #374151;
          }

          .invoice-tab svg {
            width: 16px;
            height: 16px;
            flex-shrink: 0;
          }

          .invoice-tab-content {
            padding-top: 16px;
          }

          .invoice-tab-panel {
            display: none;
          }

          .invoice-tab-panel.active {
            display: block;
          }
          
          .section-header {
            margin-bottom: 15px;
          }

          .section-title {
            font-size: 17px;
            font-weight: 600;
            line-height: 1.3;
            margin-bottom: 4px;
          }

          .section-description {
            font-size: 12px;
            color: #6b7280;
          }

          .invoice-tabs-container {
            padding-top: 0 !important;
            margin-top: 0 !important;
          }

          .project-form-card {
            padding-top: 12px;
          }

          .invoice-tabs {
            margin-bottom: 12px;
            padding-bottom: 4px;
          }

          .invoice-tab-content {
            padding-top: 20px;
          }

          .view-field {
            background-color: #f8f9fa;
            border: 1px solid #dee2e6;
            border-radius: 4px;
            padding: 8px 12px;
            min-height: 38px;
            display: flex;
            align-items: center;
          }

          .view-field {
            font-family: inherit;
            font-size: 14px;
            line-height: 1.4;
            color: #111827;
          }

          .invoice-items-table {
            width: 100%;
            margin-top: 12px;
          }

          .invoice-items-table th {
            background-color: #f8f9fa;
            padding: 10px;
            text-align: left;
            font-weight: 600;
            font-size: 13px;
            border-bottom: 2px solid #dee2e6;
          }

          .invoice-items-table td {
            padding: 10px;
            border-bottom: 1px solid #e5e7eb;
          }

          .invoice-items-table td {
            font-family: inherit;
            font-size: 14px;
            line-height: 1.45;
            color: #111827;
          }
          .invoice-items-table tbody tr:last-child td {
            border-bottom: none;
          }
        `}
      </style>

      <div className="page-container">
        <div className="project-create-container">
          {/* Form Card with Tabs */}
          <div className="project-form-card">
            {/* Stacked Sections (no tabs) */}
            <div className="invoice-sections">
              <div className="project-form-section">
                <div className="section-header">
                  <h3 className="section-title">Invoice Information</h3>
                  <p className="section-description">
                    Basic information about the invoice
                  </p>
                </div>

                <div className="form-grid-4">
                  <div className="form-group">
                    <label className="form-label">Invoice Number</label>
                    <div className="view-field">
                      {invoice.invoiceNumber || '-'}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Invoice Date</label>
                    <div className="view-field">
                      {invoice.date
                        ? new Date(invoice.date).toLocaleDateString()
                        : '-'}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Due Date</label>
                    <div className="view-field">
                      {invoice.dueDate
                        ? new Date(invoice.dueDate).toLocaleDateString()
                        : '-'}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <div className="view-field">{invoice.status || '-'}</div>
                  </div>
                </div>
              </div>

              <div className="project-form-section">
                <div className="section-header">
                  <h3 className="section-title">Client Information</h3>
                  <p className="section-description">
                    Details about the client and billing address
                  </p>
                </div>

                <div className="form-grid-4">
                  <div className="form-group">
                    <label className="form-label">Client Name</label>
                    <div className="view-field">
                      {invoice.clientName || '-'}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <div className="view-field">
                      {invoice.clientEmail || '-'}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <div className="view-field">
                      {invoice.clientPhone || '-'}
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Client Address</label>
                  <div
                    className="view-field"
                    style={{ minHeight: '80px', alignItems: 'flex-start' }}
                  >
                    {invoice.clientAddress || '-'}
                  </div>
                </div>
              </div>

              <div className="project-form-section">
                <div className="section-header">
                  <h3 className="section-title">Project Information</h3>
                  <p className="section-description">
                    Project details and description
                  </p>
                </div>

                <div className="form-grid-2">
                  <div className="form-group">
                    <label className="form-label">Project Name</label>
                    <div className="view-field">
                      {invoice.projectName || '-'}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <div className="view-field">
                      {invoice.description || '-'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="project-form-section">
                <div className="section-header">
                  <h3 className="section-title">Invoice Items</h3>
                  <p className="section-description">
                    Line items for the invoice
                  </p>
                </div>

                <table className="invoice-items-table">
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Quantity</th>
                      <th>Rate ($)</th>
                      <th>Amount ($)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(invoice.items || []).length > 0 ? (
                      invoice.items.map((item, index) => (
                        <tr key={item.id || index}>
                          <td>{item.description || '-'}</td>
                          <td>{item.quantity || 0}</td>
                          <td>{item.rate ? item.rate.toFixed(2) : '0.00'}</td>
                          <td>
                            {item.amount ? item.amount.toFixed(2) : '0.00'}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="4"
                          style={{ textAlign: 'center', color: '#6b7280' }}
                        >
                          No items found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="project-form-section">
                <div className="section-header">
                  <h3 className="section-title">Financial Summary</h3>
                  <p className="section-description">
                    Invoice totals and tax information
                  </p>
                </div>

                <div className="form-grid-4">
                  <div className="form-group">
                    <label className="form-label">Subtotal ($)</label>
                    <div className="view-field">
                      {invoice.subtotal ? invoice.subtotal.toFixed(2) : '0.00'}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Tax Amount ($)</label>
                    <div className="view-field">
                      {invoice.taxAmount
                        ? invoice.taxAmount.toFixed(2)
                        : '0.00'}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Total ($)</label>
                    <div
                      className="view-field"
                      style={{ fontWeight: '600', fontSize: '16px' }}
                    >
                      {invoice.total ? invoice.total.toFixed(2) : '0.00'}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Payment Method</label>
                    <div className="view-field">
                      {invoice.paymentMethod || '-'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="project-form-section">
                <div className="section-header">
                  <h3 className="section-title">Additional Information</h3>
                  <p className="section-description">
                    Notes, terms, and payment information
                  </p>
                </div>

                <div className="form-grid-2">
                  <div className="form-group">
                    <label className="form-label">Notes</label>
                    <div
                      className="view-field"
                      style={{ minHeight: '80px', alignItems: 'flex-start' }}
                    >
                      {invoice.notes || '-'}
                    </div>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Payment Terms</label>
                    <div
                      className="view-field"
                      style={{ minHeight: '80px', alignItems: 'flex-start' }}
                    >
                      {invoice.terms || '-'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="project-form-actions">
            <button
              type="button"
              className="btn-outline-cam"
              onClick={() => navigate('/projects/time-tracking/billing')}
            >
              <ArrowLeft size={16} />
              Back to Billing
            </button>
            <button
              type="button"
              className="btn-cam"
              onClick={() =>
                navigate(`/projects/time-tracking/billing/edit/${id}`)
              }
            >
              <Edit size={16} />
              Edit Invoice
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewInvoice;
